import { Cookie } from '@vue-util/cookie';
import { getPageViewTagData } from '@nuxt-common/add-to-bag/app/composables/useAddToBag';
import once from '~/utils/once';
import { fireLinkTag } from './tagManager';
import type { BagApiResponse } from '~/stores/useMultiATBStore';

/**
 * Fires an event when the user switches to another image in the Shop the Collection product image carousel.
 */
export const tagSTCAltImage = once(() => {
  fireLinkTag({
    event_name: 'shop the collection alt image',
    link_name: 'shop the collection alt image',
  });
});

/**
 * Fires an event when the user switches to last image in the Shop the Collection product image carousel.
 */
export const tagSTCAltLastImage = once(() => {
  fireLinkTag({
    event_name: 'shop the collection alt last image',
    link_name: 'shop the collection alt last image',
  });
});

/**
 * Fires an event when the user clicks a filter or latchkey at the top of the Shop the Collection PDP.
 */
export const tagSTCFilterClick = once(() => {
  fireLinkTag({
    event_name: 'shop the collection filter click',
    link_name: 'shop the collection filter click',
  });
});

/**
 * Fires an event when the user clicks the Select CTA.
 */
export const tagSTCSelectItem = once(() => {
  fireLinkTag({
    event_name: 'shop the collection select item',
    link_name: 'shop the collection select item',
  });
});

/**
 * Fires an event when the user clicks Clear All Selections.
 */
export const tagSTCClearAll = once(() => {
  fireLinkTag({
    event_name: 'shop the collection clear all',
    link_name: 'shop the collection clear all',
  });
});

/**
 * Fires an event when the user clicks to navigate from STC master to member PDP.
 */
export const tagSTCMemberPDPClick = () => {
  fireLinkTag({
    event_name: 'master to member pdp click',
    link_name: 'master to member pdp click',
  });
};

/**
 * Fires an event when the user clicks the Add to Bag or Buy Now CTA.
 */
export const tagSTCAddToBag = ({
  action,
  bagData,
  brandName,
  priceTypeIds,
  promoMessaging,
}: {
  action: 'add to bag' | 'buy now';
  bagData: BagApiResponse;
  brandName?: string;
  priceTypeIds?: string[];
  promoMessaging?: string[];
}) => {
  if (!bagData) return;

  const rawAnalyticsData = bagData?.meta?.analytics?.data || {};
  const productIds = rawAnalyticsData?.product_id || [];
  const numProducts = productIds.length || 1;

  const pickupEligibilities = (rawAnalyticsData.pickup_eligibility || Array(numProducts).fill('NA')) as string[];
  const emptyStringArray = Array(numProducts).fill('');

  const tagData = getPageViewTagData({
    bagData,
    options: {
      source: 'PDPSTSA2B',
      allCookies: Cookie.parse() || {},
      sourceUtagData: {
        buyNow: action === 'buy now',
        master_pdp_type: 'collection',
        bag_add_location: 'shop the collection',

        shipping_threshold: [bagData?.bagSummary?.freeShipping?.thresholdAmount || ''],
        shipping_fee: emptyStringArray,
        shipping_value: emptyStringArray,
        product_fulfillment_option: emptyStringArray,
        product_availability: emptyStringArray,
        product_availability_select: emptyStringArray,

        holiday_eligibility: emptyStringArray,
        pdp_outfitting: Array(numProducts).fill('false'),
        consolidated_product: Array(numProducts).fill('false'),
        product_type: Array(numProducts).fill('member'),
        product_promo_messaging: promoMessaging || Array(numProducts).fill('no promo messaging'),
        product_brand: Array(numProducts).fill(brandName || ''),
        pickup_eligibility: pickupEligibilities,
        ...(priceTypeIds ? { product_pricing_state: priceTypeIds } : {}),
      },
    },
  }) as Record<string, unknown>;

  fireLinkTag(tagData);
};
